import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Menu,
  MenuItem,
  ListItemText,
  Box,
  Typography,
  Divider,
  IconButton,
  Button,
  Chip,
  CircularProgress,
} from "@mui/material";
import {
  DoneAll,
  Close,
  Info,
  Warning,
  CheckCircle,
  Error as ErrorIcon,
} from "@mui/icons-material";
import { SPACING } from "../../theme/constants";
import {
  fetchNotifications,
  markAsRead,
  markAllRead,
  removeNotification,
} from "../../redux/slices/notificationSlice";

const typeIcons = {
  info: <Info fontSize="small" sx={{ color: "info.main" }} />,
  warning: <Warning fontSize="small" sx={{ color: "warning.main" }} />,
  success: <CheckCircle fontSize="small" sx={{ color: "success.main" }} />,
  error: <ErrorIcon fontSize="small" sx={{ color: "error.main" }} />,
};

const formatTime = (dateStr) => {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMin = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMin < 1) return "Just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

export default function NotificationsMenu({ anchorEl, onClose }) {
  const dispatch = useDispatch();
  const { notifications, unreadCount, loading } = useSelector(
    (state) => state.notification
  );

  useEffect(() => {
    if (anchorEl) {
      dispatch(fetchNotifications({ limit: 20 }));
    }
  }, [anchorEl, dispatch]);

  const handleMarkAllRead = () => {
    dispatch(markAllRead());
  };

  const handleMarkRead = (id) => {
    dispatch(markAsRead(id));
  };

  const handleDelete = (e, id) => {
    e.stopPropagation();
    dispatch(removeNotification(id));
  };

  return (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={onClose}
      PaperProps={{
        sx: { width: 380, maxHeight: 480, mt: SPACING.sm },
      }}
    >
      <Box
        sx={{
          p: SPACING.sm,
          pb: SPACING.xs,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Notifications
          </Typography>
          {unreadCount > 0 && (
            <Chip
              label={unreadCount}
              size="small"
              color="error"
              sx={{ height: 20, fontSize: "0.7rem" }}
            />
          )}
        </Box>
        {unreadCount > 0 && (
          <Button
            size="small"
            startIcon={<DoneAll sx={{ fontSize: 16 }} />}
            onClick={handleMarkAllRead}
            sx={{ textTransform: "none", fontSize: "0.75rem" }}
          >
            Mark all read
          </Button>
        )}
      </Box>
      <Divider />

      {loading && notifications.length === 0 ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
          <CircularProgress size={24} />
        </Box>
      ) : notifications.length === 0 ? (
        <Box sx={{ p: 3, textAlign: "center" }}>
          <Typography variant="body2" color="text.secondary">
            No notifications yet
          </Typography>
        </Box>
      ) : (
        notifications.map((notification) => (
          <MenuItem
            key={notification.id}
            onClick={() => {
              if (!notification.isRead) handleMarkRead(notification.id);
            }}
            sx={{
              py: 1.5,
              px: 2,
              alignItems: "flex-start",
              bgcolor: notification.isRead
                ? "transparent"
                : "action.hover",
              borderLeft: notification.isRead
                ? "3px solid transparent"
                : "3px solid",
              borderLeftColor: notification.isRead
                ? "transparent"
                : "primary.main",
              "&:hover": {
                bgcolor: "action.selected",
              },
            }}
          >
            <Box sx={{ mr: 1.5, mt: 0.5 }}>
              {typeIcons[notification.type] || typeIcons.info}
            </Box>
            <ListItemText
              primary={
                <Typography variant="body2" fontWeight={notification.isRead ? 400 : 600}>
                  {notification.title}
                </Typography>
              }
              secondary={
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {notification.message}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.disabled"
                    sx={{ display: "block", mt: 0.5 }}
                  >
                    {formatTime(notification.createdAt)}
                  </Typography>
                </Box>
              }
            />
            <IconButton
              size="small"
              onClick={(e) => handleDelete(e, notification.id)}
              sx={{ ml: 0.5, mt: 0.5, opacity: 0.5, "&:hover": { opacity: 1 } }}
            >
              <Close sx={{ fontSize: 14 }} />
            </IconButton>
          </MenuItem>
        ))
      )}
    </Menu>
  );
}
